/*
 *
 *  * Copyright (c) 2022 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  IterableDiffer,
  IterableDiffers,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { auditTime } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import ResizeObserver from 'resize-observer-polyfill';
import * as $moment from 'moment';

import { ContextMenuService } from '@wfm/core';

import { VirtualGridRow } from '../../types/virtual-grid-row';
import { SortCriteria } from '../../types/sort-criteria';
import { TemplateType } from '../../types/template-type';
import { VirtualGridColumn } from '../../types/virtual-grid-column';
import { VirtualGridRowHeight } from '../../types/virtual-grid-row-height';
import { ContextMenuOperation } from '../../types/context-menu-operation';
import { VirtualGridNotification, VirtualGridNotificationAction } from '../../types/virtual-grid-notification';
import { isVirtualGridGroup, VirtualGridGroup } from '../../types/virtual-grid-group';
import { FilterCriteria } from '../../filter/filter-criteria';
import { VirtualGridService } from '../../services/virtual-grid.service';
import { VirtualGridHeaderComponent } from '../virtual-grid-header/virtual-grid-header.component';
import { ChangeDetector, ItemProfile } from '@wfm/core';

@Component({
  selector: 'virtual-grid',
  templateUrl: './virtual-grid.component.html',
  styleUrls: ['./virtual-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VirtualGridComponent
  extends ChangeDetector
  implements OnInit, OnDestroy, AfterViewInit, OnChanges, DoCheck, AfterViewChecked
{
  /**
   * @Input()
   * Array containing the row data to be displayed.
   * If an item instance is a VirtualGridRow, the grid will use the item.data as the data source.
   * If an item instance is not a VirtualGridRow, the grid will use the item as the data source.
   *
   * Use a VirtualGridRow to customize a single row in the grid.
   */
  @Input() items: Array<VirtualGridRow | any> | Array<VirtualGridGroup> = [];

  /**
   * @Input()
   * Array containing the column configuration.
   */
  @Input() layout: Array<VirtualGridColumn> = [];

  /**
   * @Input()
   * It is required to identify the grid row item or else uses object reference if not provided
   */
  @Input() rowIdentifier: string = undefined;

  /**
   * @Input()
   * By default enables zebra styles for the rows display
   */
  @Input() enableZebraStyle = true;

  /**
   * @Input()
   * Enable/Desable row divider
   */
  @Input() enableRowDivider = false;

  /**
   * @Input()
   * The default grid styling provides 3 different sizes for the row: small, medium, large.
   * if the hosting component ovewrites the default styling, changing the hight of a row, than
   * an intenger number can be provided to represent the row hight in pixels.
   */
  @Input() rowHeight: VirtualGridRowHeight | number = VirtualGridRowHeight.medium;

  /**
   * @Input()
   * The groupHeaderHeight represents the height of the custom group header template
   */
  @Input()
  get groupHeaderHeight(): number {
    return this.$groupHeaderHeight || 26;
  }
  set groupHeaderHeight(value: number) {
    this.$groupHeaderHeight = value;
  }
  $groupHeaderHeight = 0;

  /**
   * @Input()
   * Template refenrece to be used as template for the cells.
   * If not provided, worklist will provide a default template.
   * If a column layout provides its own template, then the column template takes precedence
   */
  @Input() cellTemplate: TemplateType | string;

  /**
   * @Input()
   * Template reference to be used as template for the rows.
   * If not provided, worklist will provide a default template.
   * If a row provides its own template, then the row template takes precedence
   */
  @Input() rowTemplate: TemplateType;

  /**
   * @Input()
   * Template reference to be used as tempalte for the grouping header.
   * If not provided, virtual-grid will provide a default template.
   * If a grouping item provides its own template, then the grouping template takes precedence.
   */
  @Input() groupHeaderTemplate: TemplateType | string;

  /**
   * @Input()
   * Number used only to find the bottom threshold to trigger the next page event
   */
  @Input() pageSize = 50;

  /**
   * @Input()
   * Percentage of the visible last page until trigger the next page event
   */
  @Input() preFetchNextPageConfig?: number = 100;

  /**
   * @Input()
   * Boolean values to enable/disable infinite scrolling when the scroll bar reaches to the bottom threshold
   */
  @Input() infiniteScrolling?: boolean = true;

  /**
   * true - the end of the list, nextPage event is disabled
   */
  @Input() theEnd?: boolean = false;

  /**
   * @Input()
   * Boolean values to show a spinner in the grid
   * Show spinner when there is a delay in rendering the data or while loading the next page
   */
  @Input() enableNextPageLoadingSpinner?: boolean = true;

  /**
   * @Input()
   * Boolean values to show a spinner in the grid
   * Show spinner while fetching the data to the grid
   */
  @Input() loading?: boolean = false;

  /**
   * @Input()
   * selected items array
   */
  @Input() set selectedItems(items: any[]) {
    this.setSelectedItems(items);
  }
  get selectedItems(): any[] {
    return this.$selectedItems;
  }

  /**
   * @Input()
   * activated items array
   */
  @Input() set activatedItems(items: any[]) {
    this.setActivatedItems(items);
  }
  get activatedItems(): any[] {
    return this.$activatedItems;
  }

  @Input() selectedOverlay?: boolean = false;

  /**
   * @Input()
   * When using virtual grid as an Angular Component, this input is not required.
   * Host application should be responsible to configure TranslateModule and properly provide the language
   *
   * When using virtual grid as a Web Component, this input will set the current language
   * for the grid. A valid input only will be considered. Valid input is an input among of the supported languages
   */
  @Input() locale: string;

  /**
   * @Input()
   * Indicate if the header is hidden. Visible by default
   */
  @Input() hideHeader = false;

  /**
   * @Input()
   * Flag that indicates if row selection happens by a checkbox.
   * If true, an extra column is added with checkboxes, so the user can select multiple rows.
   */
  @Input() checkboxSelection = false;

  /**
   * @Input()
   * Define the most right frozen column
   * All the columns to the left, including the chosen column, will be frozen and not scrollable.
   *
   *
   * Hosting app is responsible to carefully select the frozen column considering the viewport and available space.
   * If a frozen column is beiyond the viewport, the grid might behave in a unexpected way
   */
  @Input() frozenColumn: VirtualGridColumn;

  /**
   * @Input()
   * To show a error/warning notification in the grid in case of failure scenarios
   */
  @Input() gridNotification?: VirtualGridNotification;

  /**
   * @Input()
   * Timeout delay for single click vs double click of the row item of the grid
   */
  @Input() clickTimeout = 500;

  @Input() disableContextMenu = false;
  @Input() disableHeaderContextMenu = false;

  /**
   * @Input()
   * Context Menu show up on right-click
   */
  @Input() contextMenuOperations: ContextMenuOperation[] = [];

  /**
   * @Input()
   * If specified, provides the filter criteria for the grid.
   */
  @Input() filterCriteria?: FilterCriteria[];

  /**
   * @Input()
   * sort criteria current applyed
   */
  @Input() sortCriteria: SortCriteria[];

  /**
   * @Input()
   * virtual grid top actions element template
   */
  @Input() topActionsTemplate: TemplateType;

  /**
   * @Input()
   * virtual grid row actions element template
   */
  @Input() rowActionsTemplate: TemplateType;

  /**
   * @Input()
   * first visible item in the grid.
   * attribute updated whenever a scroll event happens
   */
  @Input() set firstVisibleItem(item: any) {
    this.$scrollToItem(item);
  }

  get firstVisibleItem(): VirtualGridRow | any {
    return this.getFirstVisibleItem();
  }

  @Input() get lastVisibleItem(): VirtualGridRow | any {
    return this.getLastVisibleItem();
  }

  /**
   * @Input()
   * get current range of items in the DOM, including
   * Group and regular items
   */
  @Input() get itemsInDOM(): Array<VirtualGridRow | VirtualGridGroup | any> {
    return this.getItemsInDOM();
  }

  /**
   * @Input()
   * exposed method to host application manually trigger the change detection.
   * When an attribute value is updated inside a complex type input, like an object or array,
   * the component do not automatically detect these changes, so the host application should manually
   * trigger the change decetion inside the component to update the template
   */
  @Input() get detectChanges() {
    return () => {
      this.setFirstAndLastVisibleColumns();
      this.cd.detectChanges();
      this.virtualGridHeader?.detectChanges();
    };
  }

  /**
   * @Input()
   * exposed the method to host application for grid life cycle hooks
   */
  @Input() get lifeCycle() {
    return this.virtualGridService.lifeCycle;
  }

  /**
   * @Input()
   * expose method to scroll to index element
   * scrollToVisibility - when true, will scroll untill item is completly visible.
   * when false, will scroll untill item is in the top.
   */
  @Input() get scrollToIndex(): (index: number, scrollToVisibility?: boolean) => void {
    return this.$scrollToIndex;
  }

  /**
   * @Input()
   * expose method to scroll to item
   * scrollToVisibility - when true, will scroll untill item is completly visible.
   * when false, will scroll untill item is in the top.
   */
  @Input() get scrollToItem(): (item: any, scrollToVisibility?: boolean) => void {
    return this.$scrollToItem;
  }

  currentHoverItemIndex = -1;
  @Input() get currentHoverItem(): VirtualGridRow | any {
    return this.currentHoverItemIndex >= 0 && this.currentHoverItemIndex < this.displayedItems.length
      ? this.displayedItems[this.currentHoverItemIndex]
      : undefined;
  }

  @Input() accessibleInstitutes: { id: string }[] = [];
  @Input() instituteSeparationEnabled = false;

  @Output() currentHoverItemChange = new EventEmitter<VirtualGridRow | any>();

  /**
   * gridResized event is triggered when the grid width changes. This size change can occure due windows
   * resizing, or the space available for the grid changed by the hosting component/application.
   * The event will pass the outside grid width (element.offsetWidth) as argument.
   */
  @Output() gridResized = new EventEmitter<number>();

  /**
   * nextPage event is triggered when the scroll hits the bottom threshold distance.
   * The event passes the total length of the items input
   */
  @Output() nextPage = new EventEmitter<number>();

  /**
   * notificationAction event is triggered when user interacts with one of the notifiction actions available
   * The event passes the performed notification action
   */
  @Output() notificationAction = new EventEmitter<VirtualGridNotificationAction>();

  /**
   * selectedItemsChange event is triggered when user selects/unselects a row in the list
   *
   * Single click selects/unselects a row and unselect previous selected rows
   *
   * Single click + ctrl pressed selects/unselects a row and keeps selected previous selected rows
   *
   * Single click + shift pressed selects all rows between the clicked row and the previous selected row
   * including both ends. It also unselect any previous selected row outside this range.
   */
  @Output() selectedItemsChange = new EventEmitter<any[]>();

  /**
   * clickItem is triggered when an item is single or doubled clicked.
   * The event passes an object with index of the item, the item itself,
   * and a flag indicating if it was a single click
   */
  @Output() clickItem = new EventEmitter<{ index: number; item: any; singleClick: boolean }>();

  /**
   * columnResized event is triggered when the user resizes a column. The event object contains
   * two attributes: the new width of the column and the column configuration from the layout input
   */
  @Output() columnResized = new EventEmitter<{ width: number; column: VirtualGridColumn }>();

  /**
   * columnResizedEnd event is triggered on the mouseup event after a columnResized event happen.
   * The event object contains two attributes: the new width of the column and the column configuration
   * from the layout input that was resized
   */
  @Output() columnResizeEnd = new EventEmitter<{ width: number; column: VirtualGridColumn }>();

  /**
   * columnReordered event is triggered when the user re-order a column. The event object contains
   * three attributes: the modified columns array, previousIndex and currentIndex
   */
  @Output() columnReordered = new EventEmitter<{
    columns: Array<VirtualGridColumn>;
    previousIndex: number;
    currentIndex: number;
  }>();

  /**
   * headerContextMenuOperationSelected event is triggered when user right click over a header cell
   * and perfrom an operation from the options displayed.
   * The event object contains the menuOperation, and the column configuration where the operation was perfromed from
   */
  @Output() headerContextMenuOperationSelected = new EventEmitter<{ menuOperation: ContextMenuOperation; data: any }>();

  /**
   * groupCollased event is triggered when user collapses/uncollapses a group header.
   * Event object contains the VirtualGridGroup object, containing the collapsed flag
   */
  @Output() groupCollapsed = new EventEmitter<VirtualGridGroup>();

  /**
   * contextMenuEnabled event is triggered when user right click over a item.
   * Event object is a list of selected items.
   */
  @Output() contextMenuEnabled = new EventEmitter<any[]>();

  /**
   * contextMenuOperationSelected event is triggered when user selects an option available
   * from the context menu.
   * Event object contains the menu operation performed and the list of selected items
   */
  @Output() contextMenuOperationSelected = new EventEmitter<{ menuOperation: ContextMenuOperation; data: any }>();

  /**
   * @Output()
   * Event emitted when frozen column changes
   */
  @Output() frozenColumnChange = new EventEmitter<VirtualGridColumn>();

  /**
   * Notifies the host of a change to the filter criteria.
   */
  @Output() filterCriteriaChange: EventEmitter<FilterCriteria[]> = new EventEmitter<FilterCriteria[]>();

  /**
   * Notifies the host of a change to the sort criteria
   */
  @Output() sortCriteriaChange = new EventEmitter<SortCriteria[]>();

  @Output() errorNotify = new EventEmitter<{ message: any; error: any }>();

  /**
   * @Output()
   * Event emitted whenever the content is scrolled.
   * Current scroll position
   */
  @Output() contentScrolled = new EventEmitter<number>();

  @ViewChild(VirtualGridHeaderComponent) virtualGridHeader: VirtualGridHeaderComponent;
  @ViewChild('workListFooter') gridFooter: ElementRef;
  @ViewChild(CdkVirtualScrollViewport) viewport: CdkVirtualScrollViewport;
  @ViewChild('gridContextMenu') gridContextMenu: TemplateRef<any>;
  @ViewChildren('row') rows: QueryList<ElementRef>;
  @ViewChildren('rowGroupHeader') rowGroupHeaders: QueryList<ElementRef>;
  @ViewChildren('unfrozenRow') unfrozenRows: QueryList<ElementRef>;

  /**
   * Key:Value map object
   * Key: the index of the selected item
   * Value: item object
   *
   * Templates uses this object to check if a item is selected or not and add/remove classes
   */
  selectedItemsMap: { [key: number]: any } = {};
  activatedItemsMap: { [key: number]: any } = {};
  displayedItems: Array<VirtualGridRow | VirtualGridGroup | any> = [];
  itemsProfiles: Array<ItemProfile> = [];
  selectAll = false;
  frozenSize = 0;
  frozenIndex = -1;
  frozenColumns: Array<VirtualGridColumn> = [];
  unfrozenColumns: Array<VirtualGridColumn> = [];
  horizontalOffsetSize = 0;
  horizontalScrollSize = 0;
  horizontalScrollPosition = 0;
  verticalOffsetSize = 0;
  verticalScrollSize = 0;
  verticalScrollPosition = 0;
  nextPageSpinner = false;
  gridWidth = 0;
  rowHeightPX = 40;
  rowActionsElementRightPX = 0;

  firstVisibleColumn: VirtualGridColumn = undefined;
  lastVisibleColumn: VirtualGridColumn = undefined;

  // identify if orientation is right to left
  // used for some languages
  rtl = false;
  get rtlSignal(): -1 | 1 {
    return this.rtl ? -1 : 1;
  }

  /**
   * Last Index selected. Used to select multiple rows when pressing shift key.
   */
  private lastIndexSelected: number;
  private $selectedItems: any[] = [];
  private $activatedItems: any[] = [];
  private differ: IterableDiffer<any>;
  private differHidden: IterableDiffer<any>;
  private scrollingTimeoutHandler;
  private flattedItems: Array<VirtualGridRow | VirtualGridGroup | any> = [];
  private resizeObserver: ResizeObserver;
  private resizeObsElement: any;
  private nextPageThreshold = 0;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private element: ElementRef,
    private differs: IterableDiffers,
    private viewContainerRef: ViewContainerRef,
    private contextMenuService: ContextMenuService,
    private virtualGridService: VirtualGridService,
    private translate: TranslateService,
    cd: ChangeDetectorRef
  ) {
    super(document.defaultView, cd);
    this.differ = this.differs.find([]).create(null);
    this.differHidden = this.differs.find([]).create(null);
  }

  $scrollToIndex = (index: number, scrollToVisibility = false): void => {
    this.window.setTimeout(() => {
      if (!index || !this.items.length) {
        return this.viewport.scrollToOffset(0);
      }

      index = +index;

      const viewportSize = this.viewport.elementRef.nativeElement.clientHeight;
      const offset = this.viewport.elementRef.nativeElement.scrollTop;
      const profile = this.itemsProfiles[index];

      if (!scrollToVisibility || profile.position < offset) {
        return this.viewport.scrollToIndex(index);
      }

      const delta = profile.position + profile.height - offset - viewportSize;

      if (delta > 0) {
        this.viewport.scrollToOffset(offset + delta);
      }

      this.detectChanges();
    }, 10);
  };

  $scrollToItem = (item: any, scrollToVisibility = false) => {
    const index = this.displayedItemIndex(item);
    index >= 0 && this.$scrollToIndex(index, scrollToVisibility);
  };

  ngOnInit(): void {
    this.nextPageThreshold = Math.ceil(this.pageSize * ((100 - this.preFetchNextPageConfig) / 100));
    this.window.addEventListener('resize', this.onResized);
  }

  ngOnDestroy(): void {
    this.window.removeEventListener('resize', this.onResized);
    this.virtualGridService.lifeCycle.emit('destroy');

    this.resizeObserver.unobserve(this.resizeObsElement);
  }

  ngDoCheck(): void {
    if (this.infiniteScrolling && this.differ.diff(this.items)) {
      this.setDisplayedItems();
      this.setSelectedItems(this.selectedItems);
      this.setActivatedItems(this.activatedItems);

      this.runScrollHandler();

      this.$detectChanges();
    }

    const hidden = this.layout?.filter(c => c.isHidden) || [];
    if (this.differHidden.diff(hidden)) {
      this.calculateFrozenIndex();
    }

    const element = this.element?.nativeElement as HTMLElement;
    if (element) {
      const rtl = this.window.getComputedStyle(element).getPropertyValue('direction').toLocaleLowerCase() === 'rtl';

      if (this.rtl !== rtl) {
        this.rtl = rtl;
        this.$detectChanges();
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    changes.rowHeight && this.setItemSize();

    if (
      (changes.frozenColumn && changes.frozenColumn.currentValue !== changes.frozenColumn.previousValue) ||
      changes.layout
    ) {
      this.calculateFrozenIndex();
    }

    if (changes.layout || changes.checkboxSelection) {
      this.calculateHorizontalScroll();
    }

    if (changes.layout) {
      this.setFirstAndLastVisibleColumns();
      this.calculateRowActionsElementRight();
    }

    if (
      changes.checkboxSelection &&
      changes.checkboxSelection.currentValue !== changes.checkboxSelection.previousValue
    ) {
      this.frozenSize += this.checkboxSelection ? 42 : -42;
    }

    if (changes.items) {
      this.items = this.items || [];
      this.setDisplayedItems();
      this.setSelectedItems(this.selectedItems);
      this.setActivatedItems(this.activatedItems);
      this.runScrollHandler();
    }

    if (changes.locale && this.locale) {
      this.updateLocale();
    }

    if (
      changes.rowHeight ||
      changes.groupHeaderHeight ||
      changes.rowTemplate ||
      changes.cellTemplate ||
      changes.groupHeaderTemplate
    ) {
      this.updateItemsProfiles();
    }

    if (changes.rowTemplate) {
      this.horizontalScrollPosition = 0;
    }

    this.$detectChanges();
  }

  ngAfterViewInit(): void {
    this.checkForNextPage();

    this.viewport &&
      this.enableNextPageLoadingSpinner &&
      this.viewport.elementScrolled().subscribe(() => {
        this.nextPageSpinner = true;
        this.window.clearTimeout(this.scrollingTimeoutHandler);
        this.scrollingTimeoutHandler = this.window.setTimeout(() => {
          this.nextPageSpinner = false;
          this.$detectChanges();
        }, 100);
      });

    const sub = this.unfrozenRows.changes.pipe(auditTime(1)).subscribe(() => {
      this.calculateHorizontalScroll();
      this.calculateFrozenIndex();
      this.calculateRowActionsElementRight();
      sub.unsubscribe();
    });

    this.resizeObsElement = this.viewport.elementRef.nativeElement;
    this.resizeObserver = new ResizeObserver(() => {
      this.onResized();
    });

    this.resizeObserver.observe(this.resizeObsElement);
  }

  ngAfterViewChecked() {
    this.horizontalScrollHandlerTimer && this.horizontalScrollHandler();
  }

  private setFirstAndLastVisibleColumns() {
    this.firstVisibleColumn = undefined;
    this.lastVisibleColumn = undefined;

    if (!this.layout || !this.layout.length) return;

    const visibleColumns = this.layout.filter(c => !c.isHidden);
    this.firstVisibleColumn = visibleColumns[0];
    this.lastVisibleColumn = visibleColumns[visibleColumns.length - 1];
  }

  getFirstVisibleItem(): VirtualGridRow | any {
    return this.getVisibleItemAtPosition(this.viewport.elementRef.nativeElement.scrollTop).item;
  }

  getLastVisibleItem(): VirtualGridRow | any {
    const position =
      this.viewport.elementRef.nativeElement.scrollTop + this.viewport.elementRef.nativeElement.clientHeight;
    const index = this.getVisibleItemAtPosition(position).index - 1;
    return this.displayedItems[index];
  }

  private getVisibleItemAtPosition(position: number): { item: VirtualGridRow | any; index: number } {
    let index = this.viewport.getRenderedRange()?.start || 0;

    let item: ItemProfile = this.itemsProfiles[index];
    while (
      index >= 0 &&
      index < this.itemsProfiles.length &&
      (position < item.position || position >= item.position + item.height)
    ) {
      index += position < item.position ? -1 : 1;
      item = this.itemsProfiles[index];
    }

    while (isVirtualGridGroup(this.displayedItems[index])) {
      index++;
    }

    return {
      item: this.displayedItems[index],
      index
    };
  }

  getItemsInDOM(): Array<VirtualGridRow | VirtualGridGroup | any> {
    const range = this.viewport.getRenderedRange();
    return (this.displayedItems || []).slice(range.start, range.end + 1);
  }

  changeFrozenColumn(frozenColumn: VirtualGridColumn) {
    this.frozenColumn = frozenColumn;
    this.frozenColumnChange.emit(this.frozenColumn);
    this.calculateFrozenIndex();
  }

  reorderedColumn(event: { columns: VirtualGridColumn[]; previousIndex: number; currentIndex: number }) {
    this.calculateFrozenIndex();
    this.columnReordered.emit(event);
  }

  selectHeaderContextMenuOperation($event: { menuOperation: ContextMenuOperation; data: any }) {
    $event.menuOperation.id === 'hide' && this.calculateFrozenIndex();
    this.headerContextMenuOperationSelected.emit($event);
  }

  private clicks = 0;
  private clickTimer = undefined;

  click(e, item: any, index: number): void {
    if (e.ctrlKey || e.shiftKey) {
      !this.checkboxSelection && this.selectItem(e, item, index);
      return;
    }

    this.clicks++;

    if (this.clicks == 1) {
      !this.checkboxSelection && this.selectItem(e, item, index);

      this.clickTimer = this.window.setTimeout(() => {
        this.clickItem.emit({ index, item, singleClick: true });
        this.window.clearTimeout(this.clickTimer);
        this.clicks = 0;
      }, this.clickTimeout);
    } else {
      this.clickItem.emit({ index, item, singleClick: false });

      this.window.clearTimeout(this.clickTimer);
      this.clicks = 0;
    }
  }

  setItemSize() {
    this.rowHeightPX = +this.rowHeight || 40;

    if (this.rowHeight === VirtualGridRowHeight.large) {
      this.rowHeightPX = 56;
    }
    if (this.rowHeight === VirtualGridRowHeight.medium) {
      this.rowHeightPX = 48;
    }
  }

  private displayedItemIndex(item: any): number {
    if (!item) return -1;

    return this.displayedItems.findIndex(($item: any) =>
      this.rowIdentifier ? $item[this.rowIdentifier] === item[this.rowIdentifier] : $item === item
    );
  }

  private setSelectedItems(items: any[]) {
    items = items || [];
    this.selectedItemsMap = {};
    this.$selectedItems = [...items];

    for (const item of items) {
      const index = this.displayedItemIndex(item);
      if (index >= 0) {
        this.selectedItemsMap[index] = item;
      }
    }

    if (this.$selectedItems.length === 0) {
      this.lastIndexSelected = 0;
    }
  }

  private setActivatedItems(items: any[]) {
    this.activatedItemsMap = {};
    this.$activatedItems = [...items];

    for (const item of items) {
      const index = this.displayedItemIndex(item);
      if (index >= 0) {
        this.activatedItemsMap[index] = item;
      }
    }
  }

  selectItem = (e, item: any, index: number, multiple = false) => {
    // if running in a MAC OS, consider the meta key as ctrl.
    const ctrl = (this.window.navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? e.metaKey : e.ctrlKey) || multiple;
    const shift = e.shiftKey;

    if (ctrl) {
      if (this.selectedItemsMap[index]) {
        delete this.selectedItemsMap[index];
        this.selectedItems.splice(this.selectedItems.indexOf(item), 1);
      } else {
        this.selectedItemsMap[index] = item;
        this.selectedItems.push(item);
      }

      this.lastIndexSelected = this.selectedItemsMap[index] && index;
    } else if (shift) {
      this.document.getSelection().removeAllRanges();
      let firstSelectedIndex = this.items.indexOf(this.selectedItems[0]);
      firstSelectedIndex = firstSelectedIndex >= 0 ? firstSelectedIndex : 0;
      const lastIndexSelected = this.lastIndexSelected;
      const ind_0 = lastIndexSelected || firstSelectedIndex || 0;
      const ind_n = index;
      const d = ind_n - ind_0;

      this.selectedItemsMap = {};
      this.selectedItems = [];

      for (let i = 0; i <= Math.abs(d); i++) {
        const indAdd = ind_0 + i * (d >= 0 ? 1 : -1);
        if (!isVirtualGridGroup(this.displayedItems[indAdd])) {
          this.selectedItemsMap[indAdd] = this.displayedItems[indAdd];
          this.selectedItems.push(this.displayedItems[indAdd]);
        }
      }

      this.lastIndexSelected = lastIndexSelected;
    } else {
      this.selectedItemsMap = { [index]: item };
      this.selectedItems = [item];

      this.lastIndexSelected = this.selectedItemsMap[index] && index;
    }

    this.updateSelectAll();
    isVirtualGridGroup(this.items[0]) && this.updateSelectAllGroups(this.items);
    this.selectedItemsChange.emit(this.selectedItems);

    this.$detectChanges();
  };

  private updateSelectAll() {
    const items = this.flattedItems.filter(item => !isVirtualGridGroup(item));
    this.selectAll = items.length === Object.keys(this.selectedItemsMap).length;
  }

  private updateSelectAllGroups(items: Array<VirtualGridRow | any> | Array<VirtualGridGroup>): boolean {
    if (isVirtualGridGroup(items[0])) {
      let selected = true;

      for (const g of items) {
        const group = g as VirtualGridGroup;
        group.selected = this.updateSelectAllGroups(group.items);
        selected = selected && group.selected;
      }

      return selected;
    }

    for (const item of items) {
      // if any item is not selected, then not all items are selected. return false
      if (this.selectedItems.indexOf(item) < 0) {
        return false;
      }
    }

    // if all items are selected. return true
    return true;
  }

  selectAllChange(selectAll: boolean) {
    this.selectAll = selectAll;
    this.selectedItems = (selectAll && this.flattedItems.filter(item => !isVirtualGridGroup(item))) || [];

    isVirtualGridGroup(this.items[0]) && this.updateSelectAllGroups(this.items);

    this.selectedItemsChange.emit(this.selectedItems);

    this.$detectChanges();
  }

  selectgroup(e, group: VirtualGridGroup) {
    const items = this.flatGroupedItems(group.items, group.level + 1, 0, true).flattened;

    group.selected = !group.selected;

    if (group.selected) {
      this.selectedItems = [
        ...this.selectedItems,
        ...items.filter(item => !isVirtualGridGroup(item) && this.selectedItems.indexOf(item) < 0)
      ];
      items
        .filter(item => isVirtualGridGroup(item))
        .forEach(item => {
          item.selected = true;
        });
    } else {
      const selectedItems = [...this.selectedItems];
      items
        .map(item => this.selectedItems.indexOf(item))
        .sort((a, b) => +a - +b)
        .reverse()
        .forEach(index => index >= 0 && selectedItems.splice(index, 1));
      this.selectedItems = selectedItems;
      items
        .filter(item => isVirtualGridGroup(item))
        .forEach(item => {
          item.selected = false;
        });
    }

    this.updateSelectAll();
    this.selectedItemsChange.emit(this.selectedItems);
    this.$detectChanges();
  }

  private checkForNextPage() {
    if (!this.infiniteScrolling || this.theEnd || this.gridNotification) return;

    let bottom = this.verticalScrollPosition + this.viewport.elementRef.nativeElement.clientHeight;
    bottom = this.getVisibleItemAtPosition(bottom).index;

    if (this.nextPageThreshold >= this.viewport.getDataLength() - bottom) {
      this.nextPage.emit(this.items && this.items.length);
    }
  }

  private scrolledByApplication = 0;
  scrollHandler = ($event: any) => {
    this.contentScrolled.emit(this.verticalScrollPosition);
    this.checkForNextPage();

    if (this.scrolledByApplication && $event.target.scrollTop !== this.verticalScrollPosition) {
      $event.target.scrollTop = this.verticalScrollPosition;
      $event.stopPropagation();
      $event.preventDefault();
      return;
    }

    this.runScrollHandler(100);
  };

  private verticalScrollHandlerTimer = 0;
  private horizontalScrollHandlerTimer = 0;
  private runScrollHandler(interval = 0) {
    this.verticalScrollHandlerTimer && this.window.clearTimeout(this.verticalScrollHandlerTimer);
    this.verticalScrollHandlerTimer = this.window.setTimeout(() => {
      if (!this.wheelTimer) {
        this.verticalScrollPosition = this.viewport.elementRef.nativeElement.scrollTop;
      }
    }, interval);

    this.horizontalScrollHandlerTimer && this.window.clearTimeout(this.horizontalScrollHandlerTimer);
    this.horizontalScrollHandlerTimer = this.window.setTimeout(() => this.horizontalScrollHandler(), 0);
  }

  private wheelTimer = 0;
  private wheelDelta = 0;

  private horizontalScrollHandler() {
    const rows = this.unfrozenRows.toArray() || [];
    for (const row of rows) {
      row.nativeElement.scrollLeft = this.horizontalScrollPosition * this.rtlSignal;
    }
  }

  onWheel($event: WheelEvent) {
    if (!$event.deltaY) {
      return;
    }

    $event.preventDefault();
    $event.stopPropagation();

    if (this.verticalScrollSize === this.verticalOffsetSize) {
      // then no vertical scroll bar
      // this scenario means there are many columns, but only a few rows
      // the mouse wheel should perform horizontal scroll instead of vertical scroll
      this.horizontalScrollPosition += $event.deltaY;
      this.horizontalScrollPosition = Math.min(
        this.horizontalScrollSize - this.horizontalOffsetSize,
        Math.max(0, this.horizontalScrollPosition)
      );
    } else {
      this.verticalScrollPosition += $event.deltaY;
      this.wheelDelta += $event.deltaY;

      this.verticalScrollPosition = Math.min(
        this.verticalScrollSize - this.verticalOffsetSize,
        Math.max(0, this.verticalScrollPosition)
      );

      if (!this.wheelTimer) {
        this.wheelTimer = this.window.setTimeout(() => {
          this.scrollToOffset(this.viewport.elementRef.nativeElement.scrollTop + this.wheelDelta);

          this.wheelTimer = 0;
          this.wheelDelta = 0;
        }, 100);
      }
    }
  }

  onMouseLeave() {
    this.currentHoverItemIndex = -1;
    this.currentHoverItemChange.emit(this.currentHoverItem);
  }

  onGroupMouseEnter() {
    this.currentHoverItemIndex = -1;
    this.currentHoverItemChange.emit(this.currentHoverItem);
  }

  onGridRowMouseEnter(rowIndex: number) {
    this.currentHoverItemIndex = rowIndex;
    this.currentHoverItemChange.emit(this.currentHoverItem);
  }

  scrollToOffset(offset: number) {
    this.viewport.scrollToOffset(offset, 'auto');
    this.window.clearTimeout(this.scrolledByApplication);
    this.scrolledByApplication = this.window.setTimeout(() => {
      this.scrolledByApplication = 0;
    }, 500);

    this.detectChanges();
  }

  verticalScrollPositionChange(position: number) {
    this.verticalScrollPosition = position;
    this.scrollToOffset(position);
  }

  horizontalScrollPositionChange() {
    this.unfrozenRows
      .toArray()
      .map(row => row.nativeElement)
      .forEach(ele => {
        ele.scrollLeft = this.horizontalScrollPosition * this.rtlSignal;
      });

    this.detectChanges();
  }

  unfrozenScrollHandler($event: Event) {
    const target = $event.target as HTMLElement;
    this.horizontalScrollPosition = target.scrollLeft * this.rtlSignal;
  }

  private gridContainerWidth = 0;
  onResized = () => {
    if (this.gridContainerWidth !== this.viewport.elementRef.nativeElement.parentElement.clientWidth) {
      this.gridWidth = this.viewport.elementRef.nativeElement.clientWidth;

      this.gridResized.emit(this.viewport.elementRef.nativeElement.parentElement.offsetWidth);

      this.updateItemsProfiles();

      this.calculateHorizontalScroll();
      this.calculateVerticalScroll();
      this.calculateRowActionsElementRight();

      this.closeContextMenu();

      this.$detectChanges();
    }
  };

  onColumnResized = () => {
    this.calculateHorizontalScroll();
    this.calculateRowActionsElementRight();
  };

  contextMenuSelected = (menuOperation: ContextMenuOperation) => {
    if (!menuOperation.subMenu || !menuOperation.subMenu.length) {
      this.closeContextMenu(menuOperation);
      this.contextMenuOperationSelected.emit({
        menuOperation,
        data: this.selectedItems
      });
    }
  };

  openContextMenu(e: MouseEvent, item: any, index: number) {
    if (!this.disableContextMenu) {
      if (!this.selectedItemsMap[index]) {
        this.selectItem(e, item, index);
      }

      this.contextMenuEnabled.emit(this.selectedItems);

      this.contextMenuService.openContextMenu(
        this.document.children[0].getBoundingClientRect(), // using root element as boundary
        e,
        this.gridContextMenu,
        this.viewContainerRef
      );
    }
    e.preventDefault();
  }

  closeContextMenu(data?: any) {
    this.contextMenuService && this.contextMenuService.close(data);
  }

  notificationActionClicked() {
    this.notificationAction.emit(this.gridNotification.notificationAction);
  }

  private calculateFrozenIndex() {
    let recalculate = false;

    const oldFrozenIndex = this.frozenIndex;

    if (this.layout && this.layout.length) {
      this.frozenIndex = this.layout.indexOf(this.frozenColumn);

      if (this.frozenIndex >= 0) {
        this.frozenColumns = this.layout.slice(0, this.frozenIndex + 1);
        this.unfrozenColumns = this.layout.slice(this.frozenIndex + 1);

        const row: HTMLElement = this.rows.first?.nativeElement;
        let size = 0;
        for (const column of this.frozenColumns) {
          if (!column.isHidden && row) {
            const cell: HTMLElement = row.querySelector(`.virtual-grid-cell-${column.propertyName}`);
            size += cell?.offsetWidth || 0;
            recalculate = recalculate || !cell;
          }
        }
        this.frozenSize = size + (this.checkboxSelection ? 42 : 0);
      } else {
        this.frozenColumns = [];
        this.unfrozenColumns = this.layout;
        this.frozenSize = this.checkboxSelection ? 42 : 0;
      }
    } else {
      this.unfrozenColumns = this.layout;
      this.frozenIndex = -1;
    }

    // recalculate to get size for new elementes visible
    if (recalculate) {
      this.window.setTimeout(() => this.calculateFrozenIndex(), 0);
    }

    oldFrozenIndex !== this.frozenIndex && this.$detectChanges();
  }

  cellStringTemplate = (context: any, template: string): string => {
    return template.replace(/\{\{DATA\}\}/g, context.data);
  };

  groupStringTemplate = (context: any, template: string): string => {
    return template.replace(/\{\{LABEL\}\}/g, context.group.label).replace(/\{\{DATA\}\}/g, context.group.data);
  };

  toggleGroupCollapsed(group: VirtualGridGroup) {
    group.collapsed = !group.collapsed;

    // current visible items from this group
    const groupIndex = this.displayedItems.indexOf(group);
    const groupProfile = this.flatGroupedItems(
      group.items,
      group.level + 1,
      this.itemsProfiles[groupIndex].position + this.itemsProfiles[groupIndex].height
    );
    const last = groupProfile.profiles[groupProfile.profiles.length - 1];
    const delta = last?.position + last?.height - groupProfile.profiles[0]?.position;

    if (group.collapsed && groupProfile.profiles.length) {
      // remove items from array
      this.displayedItems.splice(groupIndex + 1, groupProfile.flattened.length);
      this.itemsProfiles.splice(groupIndex + 1, groupProfile.flattened.length);
      for (let i = groupIndex + 1; i < this.itemsProfiles.length; i++) {
        this.itemsProfiles[i].position -= delta;
      }
    } else if (groupProfile.profiles.length) {
      // add items on array
      this.displayedItems.splice(groupIndex + 1, 0, ...groupProfile.flattened);
      this.itemsProfiles.splice(groupIndex + 1, 0, ...groupProfile.profiles);
      for (let i = groupIndex + 1 + groupProfile.profiles.length; i < this.itemsProfiles.length; i++) {
        this.itemsProfiles[i].position += delta;
      }
    }

    this.displayedItems = [...this.displayedItems];
    this.itemsProfiles = [...this.itemsProfiles];

    this.groupCollapsed.emit(group);

    // to triger the selectedItems seter
    this.setSelectedItems(this.selectedItems);
    this.setActivatedItems(this.activatedItems);

    this.updateSelectAll();
    isVirtualGridGroup(this.items[0]) && this.updateSelectAllGroups(this.items);

    this.calculateVerticalScroll();

    this.$detectChanges();
  }

  private validateItemsList(items: Array<VirtualGridRow | any> | Array<VirtualGridGroup>) {
    const isGroupType = isVirtualGridGroup(items[0]);

    for (const item of items) {
      if (isVirtualGridGroup(item) !== isGroupType) {
        throw new TypeError(
          'items input contains wrong item types. It should be Array<VirtualGridRow | any> | Array<VirtualGridGroup>.'
        );
      }

      if (isGroupType && item.items && item.items.length) {
        this.validateItemsList(item.items);
      }
    }
  }

  /**
   * given an array of items or groups,
   * return the flatted array of visible items
   */

  private flatGroupedItems(
    items: Array<VirtualGridRow | any> | Array<VirtualGridGroup> = [],
    level = 0,
    offset = 0,
    ignoreCollapsed?: boolean
  ): {
    flattened: Array<VirtualGridRow | any | VirtualGridGroup>;
    profiles: Array<ItemProfile>;
  } {
    const flattened: Array<VirtualGridRow | any | VirtualGridGroup> = [];
    const profiles: Array<ItemProfile> = [];
    const isGroupType = isVirtualGridGroup(items[0]);

    if (!isGroupType) {
      return {
        flattened: items,
        profiles: (items as Array<any>).map((item, i) => ({
          group: false,
          position: i * this.rowHeightPX + offset,
          height: this.rowHeightPX
        }))
      };
    }

    for (const group of items) {
      flattened.push(group);
      profiles.push({ group: true, position: offset, height: this.groupHeaderHeight });
      group.level = level;
      if (!(group as VirtualGridGroup).collapsed || ignoreCollapsed) {
        const result = this.flatGroupedItems(
          (group as VirtualGridGroup).items,
          level + 1,
          offset + this.groupHeaderHeight,
          ignoreCollapsed
        );
        flattened.push(...result.flattened);
        profiles.push(...result.profiles);
      }
      offset = profiles[profiles.length - 1].position + profiles[profiles.length - 1].height;
    }

    return { flattened, profiles };
  }

  private updateItemsProfiles() {
    let previous: ItemProfile = null;
    for (let i = 0; i < this.itemsProfiles.length; i++) {
      this.itemsProfiles[i].position = (previous && previous.position + previous.height) || 0;
      this.itemsProfiles[i].height = (this.itemsProfiles[i].group && this.groupHeaderHeight) || this.rowHeightPX;
      previous = this.itemsProfiles[i];
    }

    this.itemsProfiles = [...this.itemsProfiles];

    this.calculateVerticalScroll();
  }

  private setDisplayedItems() {
    this.validateItemsList(this.items);

    this.flattedItems = this.flatGroupedItems(this.items, 0, 0, true).flattened;
    const displayedItems = this.flatGroupedItems(this.items);

    this.displayedItems = [...displayedItems.flattened];
    this.itemsProfiles = displayedItems.profiles;

    this.$detectChanges();
    this.calculateVerticalScroll();
  }

  private horizontalScrollCalculation;

  private calculateHorizontalScroll() {
    this.horizontalScrollCalculation && this.window.clearTimeout(this.horizontalScrollCalculation);

    this.horizontalScrollCalculation = this.window.setTimeout(() => {
      const headerOffsetSize = this.virtualGridHeader?.unfrozenElement?.nativeElement?.offsetWidth || 0;
      const headerScrollSize = this.virtualGridHeader?.unfrozenElement?.nativeElement?.scrollWidth || 0;
      const row = this.unfrozenRows.first;

      if (
        this.unfrozenRows.length > 0 &&
        !this.gridNotification &&
        row.nativeElement.offsetWidth > 0 &&
        row.nativeElement.scrollWidth > 0
      ) {
        this.horizontalOffsetSize = row.nativeElement.offsetWidth;
        this.horizontalScrollSize = row.nativeElement.scrollWidth;
      } else {
        this.horizontalOffsetSize = headerOffsetSize;
        this.horizontalScrollSize = headerScrollSize;
      }

      this.horizontalScrollCalculation = undefined;

      this.$detectChanges();
    }, 150);
  }

  private verticalScrollCalculation;

  private calculateVerticalScroll() {
    this.verticalScrollCalculation && this.window.clearTimeout(this.verticalScrollCalculation);

    this.verticalScrollCalculation = this.window.setTimeout(() => {
      this.verticalOffsetSize = 0;
      this.verticalScrollSize = 0;
      this.verticalScrollPosition = 0;

      if (this.displayedItems?.length) {
        const element: HTMLElement = this.viewport.elementRef.nativeElement;
        this.verticalOffsetSize = element.offsetHeight;
        this.verticalScrollSize = element.scrollHeight;
        this.verticalScrollPosition = element.scrollTop;
      }

      this.verticalScrollCalculation = undefined;
      this.checkForNextPage();
      this.$detectChanges();
    }, 150);
  }

  private updateLocale() {
    const reg = new RegExp(this.translate.getLangs().join('|'), 'g');
    if (this.locale.match(reg)) {
      this.translate.use(this.locale);
      $moment.locale(this.locale);
    }
  }

  private calculateRowActionsElementRight() {
    if (this.rowActionsTemplate === null || this.rowActionsTemplate === undefined) {
      return;
    }

    const row: HTMLElement = this.rows?.first?.nativeElement;
    const lastColumn: HTMLElement = row?.querySelector('.virtual-grid-cell-last');
    const lastColumnRight = lastColumn?.getBoundingClientRect()?.right;
    const rowRight = row?.getBoundingClientRect()?.right;
    if (lastColumnRight < rowRight) {
      this.rowActionsElementRightPX = rowRight - lastColumnRight || 0;
    } else {
      this.rowActionsElementRightPX = 0;
    }
  }
}
